import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CheckCircle2, AlertTriangle, XCircle, RefreshCw, Loader2, ArrowRight } from "lucide-react";
import { getIntegrationsHealth } from "@/lib/apiClient";
import { ProviderLogo } from "@/components/ProviderLogo";
import { cn } from "@/lib/utils";

const TONE = {
  ok: { dot: "bg-emerald-500", ring: "ring-emerald-500/30", text: "text-emerald-600 dark:text-emerald-400", Icon: CheckCircle2, label: "All accounting syncs healthy" },
  warning: { dot: "bg-amber-500", ring: "ring-amber-500/40", text: "text-amber-600 dark:text-amber-400", Icon: AlertTriangle, label: "Accounting sync needs attention" },
  error: { dot: "bg-red-500", ring: "ring-red-500/40", text: "text-red-600 dark:text-red-400", Icon: XCircle, label: "Accounting sync has failures" },
};

const ago = (iso) => {
  if (!iso) return "never";
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  return m < 1 ? "just now" : m < 60 ? `${m} min ago` : m < 1440 ? `${Math.round(m / 60)} h ago` : `${Math.round(m / 1440)} d ago`;
};

const ProviderRow = ({ p, onNavigate }) => {
  const t = TONE[p.state];
  return (
    <div className="py-2.5" data-testid={`sync-health-${p.provider}`}>
      <div className="flex items-center gap-2 text-sm">
        <ProviderLogo provider={p.provider} className="h-4 w-4 rounded-sm" />
        <span className="font-medium">{p.label}</span>
        <span className="text-xs text-muted-foreground truncate">{p.tenant_name}</span>
        <span className={cn("ml-auto inline-flex items-center gap-1 text-xs", t.text)}><span className={cn("h-2 w-2 rounded-full", t.dot)} />{p.state === "ok" ? "Healthy" : p.state === "warning" ? "Attention" : "Failing"}</span>
      </div>
      {p.issues.length ? (
        <ul className="mt-1.5 space-y-1.5">
          {p.issues.map((i) => (
            <li key={i.code} className="text-xs" data-testid={`sync-health-issue-${p.provider}-${i.code}`}>
              <div className="flex items-start gap-1.5">
                <span className={cn("mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full", i.severity === "error" ? "bg-red-500" : "bg-amber-500")} />
                <span className="min-w-0 flex-1">{i.message}</span>
                <Link to={i.link} onClick={onNavigate} className="inline-flex shrink-0 items-center gap-0.5 font-medium text-primary hover:underline" data-testid={`sync-health-fix-${p.provider}-${i.code}`}>
                  {i.action || "Fix"} <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
              {i.invoices?.length > 1 && (
                <div className="ml-3 mt-1 flex flex-wrap gap-1" data-testid={`sync-health-invoices-${p.provider}-${i.code}`}>
                  {i.invoices.slice(0, 4).map((v) => (
                    <Link key={v.id} to={`/invoice/${v.id}`} onClick={onNavigate} className="rounded border border-border px-1.5 py-0.5 font-mono text-[10px] hover:border-primary hover:text-primary" title={v.supplier} data-testid={`sync-health-invoice-${v.id}`}>
                      {v.invoice_number || v.supplier || "invoice"}
                    </Link>
                  ))}
                  {i.count > 4 && <span className="px-1 text-[10px] text-muted-foreground">+{i.count - 4} more</span>}
                </div>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-1 text-xs text-muted-foreground">{p.synced_count} sent · payments checked {ago(p.last_payment_check_at)}</p>
      )}
    </div>
  );
};

export const SyncHealthBadge = () => {
  const [h, setH] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = React.useCallback(() => { setBusy(true); return getIntegrationsHealth().then(setH).catch(() => {}).finally(() => setBusy(false)); }, []);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
  if (!h || h.state === "none") return null;
  const t = TONE[h.state];
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="sync-health-badge" data-state-health={h.state} title={t.label} aria-label={t.label}
          className={cn("relative flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors")}>
          <RefreshCw className="h-4 w-4" />
          <span className={cn("absolute right-1.5 top-1.5 h-2.5 w-2.5 rounded-full ring-2 ring-background", t.dot, h.state !== "ok" && "animate-pulse")} data-testid="sync-health-dot" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-3" data-testid="sync-health-popover">
        <div className={cn("flex items-center gap-2 text-sm font-medium", t.text)}><t.Icon className="h-4 w-4" /> {t.label}
          <button onClick={load} className="ml-auto text-muted-foreground hover:text-foreground" title="Re-check" data-testid="sync-health-refresh">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}</button>
        </div>
        <div className="mt-1 divide-y divide-border">{h.providers.map((p) => <ProviderRow key={p.provider} p={p} onNavigate={() => setOpen(false)} />)}</div>
        <Link to="/settings?tab=xero" onClick={() => setOpen(false)} className="mt-2 block text-center text-xs text-primary hover:underline" data-testid="sync-health-settings-link">Open accounting settings</Link>
      </PopoverContent>
    </Popover>
  );
};
